import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Store, ShieldCheck, GitPullRequest, Layers, MapPin, Users, History,
  LogOut, Link2, FileUp, Search, Mail,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/admin/listings", label: "Listings", icon: Store, testid: "nav-listings" },
  { to: "/admin/verification", label: "Verification Queue", icon: ShieldCheck, badge: "pendingVerifications", testid: "nav-verification" },
  { to: "/admin/category-requests", label: "Category Requests", icon: GitPullRequest, badge: "pendingCategoryRequests", testid: "nav-category-requests" },
  { to: "/admin/categories", label: "Categories", icon: Layers, testid: "nav-categories" },
  { to: "/admin/cities", label: "Cities", icon: MapPin, testid: "nav-cities" },
  { to: "/admin/employees", label: "Employees", icon: Users, adminOnly: true, testid: "nav-employees" },
  { to: "/admin/subscribers", label: "Subscribers", icon: Mail, adminOnly: true, testid: "nav-subscribers" },
  { to: "/admin/activity", label: "Activity Log", icon: History, testid: "nav-activity" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const loadCounts = () => api.get("/nav-counts").then((r) => setCounts(r.data)).catch(() => {});
  useEffect(() => {
    loadCounts();
    const t = setInterval(loadCounts, 30000);
    return () => clearInterval(t);
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/admin/listings?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="flex min-h-screen bg-[#F5F7FA]">
      <aside className="w-64 shrink-0 bg-[#1B2A4A] text-white flex flex-col fixed inset-y-0 z-30">
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-white/10">
          <div className="w-8 h-8 rounded-lg bg-[#F5961B] flex items-center justify-center">
            <Link2 className="w-4.5 h-4.5 text-white" size={18} />
          </div>
          <div className="leading-tight">
            <div className="font-bold text-sm tracking-wide" data-testid="sidebar-logo">BROWARD <span className="text-[#5FA3E8]">LOCAL</span></div>
            <div className="text-[10px] text-white/50 uppercase tracking-widest">Admin Console</div>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-[#2E7BD6] text-white" : "text-white/70 hover:bg-white/[0.08] hover:text-white"
                }`
              }
            >
              <n.icon size={17} />
              <span className="flex-1">{n.label}</span>
              {n.badge && counts[n.badge] > 0 && (
                <span data-testid={`${n.testid}-badge`} className="bg-[#F5961B] text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                  {counts[n.badge]}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <NavLink to="/admin/import" data-testid="nav-csv-import" className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive ? "bg-[#2E7BD6] text-white" : "text-white/70 hover:bg-white/[0.08] hover:text-white"
            }`
          }>
            <FileUp size={17} /> CSV Import
          </NavLink>
        </div>
      </aside>

      <div className="flex-1 ml-64 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-[#E2E8F0] flex items-center gap-4 px-6 sticky top-0 z-20">
          <form onSubmit={onSearch} className="flex-1 max-w-md">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input
                data-testid="global-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search listings..."
                className="w-full h-9 pl-9 pr-3 rounded-lg border border-[#E2E8F0] bg-[#F5F7FA] text-sm outline-none focus:border-[#2E7BD6] focus:ring-2 focus:ring-[#2E7BD6]/20 transition"
              />
            </div>
          </form>
          <div className="flex-1" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-button" className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-[#F5F7FA] transition-colors">
                <div className="w-8 h-8 rounded-full bg-[#1B2A4A] text-white flex items-center justify-center text-xs font-bold">
                  {user?.name?.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <div className="text-left hidden sm:block">
                  <div className="text-sm font-semibold text-[#0F172A] leading-tight">{user?.name}</div>
                  <div className="text-[11px] text-[#94A3B8] capitalize">{user?.role}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-red-600 cursor-pointer">
                <LogOut size={15} className="mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 p-6 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
